/** Virtual zone key for reservations without room/table (not a real VenueRoom name). */
export const UNASSIGNED_ZONE_KEY = '__unassigned__';
export const UNASSIGNED_ZONE_LABEL = 'Sin asignar';

export function isUnassignedZoneKey(zoneKey) {
	return zoneKey === UNASSIGNED_ZONE_KEY;
}

export function zoneLabelForKey(zoneKey, tables, roomLabelForSystemName) {
	if (isUnassignedZoneKey(zoneKey)) {
		return UNASSIGNED_ZONE_LABEL;
	}
	return roomLabelForSystemName(tables, zoneKey);
}

/**
 * Reservation has no room and no table assigned (Restoo/floorplan allocation failure).
 */
export function isUnassignedReservation(reservation) {
	if (!reservation) return false;
	const hasTable =
		reservation.table != null && reservation.table !== '' ||
		(Array.isArray(reservation.tables) && reservation.tables.length > 0);
	return !reservation.roomName && !hasTable;
}

/** Zone key used for grouping/filtering (real room name or virtual unassigned key). */
export function reservationZoneKey(reservation) {
	return isUnassignedReservation(reservation) ? UNASSIGNED_ZONE_KEY : reservation.roomName;
}

export function collectReservationTableNames(reservation) {
	const candidates = new Set();
	if (reservation?.table != null && reservation.table !== '') {
		candidates.add(String(reservation.table).trim());
	}
	if (Array.isArray(reservation?.tables)) {
		reservation.tables.forEach((t) => {
			const name = String(t?.name ?? t).trim();
			if (name) candidates.add(name);
		});
	}
	return candidates;
}

/**
 * Unassigned reservations always pass zone/range filters so staff can fix them.
 */
export function reservationMatchesZoneOrRangeFilter(reservation, selectedZones, allowedTables) {
	if (isUnassignedReservation(reservation)) {
		return true;
	}

	const zones = Array.isArray(selectedZones) ? selectedZones : [];
	const tableSet = allowedTables instanceof Set ? allowedTables : new Set(allowedTables || []);

	if (zones.length === 0 && tableSet.size === 0) {
		return true;
	}

	const matchZone = zones.includes(reservation.roomName);
	let matchRange = false;
	if (tableSet.size > 0) {
		for (const tableName of collectReservationTableNames(reservation)) {
			if (tableSet.has(tableName)) {
				matchRange = true;
				break;
			}
		}
	}

	return matchZone || matchRange;
}

export function isUnassignedNotification(notification) {
	if (!notification) return false;
	if (notification.reservation && isUnassignedReservation(notification.reservation)) {
		return true;
	}
	const rooms = notification.rooms;
	if (!Array.isArray(rooms) || rooms.length === 0) {
		return false;
	}
	return rooms.every(
		(room) => !room?.name && (!Array.isArray(room?.tables) || room.tables.length === 0),
	);
}

/**
 * Collects table-name candidates from singular/plural reservation payloads and rooms.
 * @param {object} notification
 * @returns {Set<string>}
 */
export function collectNotificationTableNames(notification) {
	const candidates = new Set();
	if (notification?.tableNumber != null && notification.tableNumber !== '') {
		candidates.add(String(notification.tableNumber).trim());
	}
	const pushReservationTables = (reservation) => {
		if (!reservation) return;
		if (reservation.table != null && reservation.table !== '') {
			candidates.add(String(reservation.table).trim());
		}
		if (Array.isArray(reservation.tables)) {
			reservation.tables.forEach((t) => {
				const name = String(t?.name ?? t).trim();
				if (name) candidates.add(name);
			});
		}
	};
	pushReservationTables(notification?.reservation);
	if (Array.isArray(notification?.reservations)) {
		notification.reservations.forEach(pushReservationTables);
	}
	if (Array.isArray(notification?.rooms)) {
		notification.rooms.forEach((room) => {
			if (Array.isArray(room?.tables)) {
				room.tables.forEach((t) => {
					const name = String(t?.name ?? t).trim();
					if (name) candidates.add(name);
				});
			}
		});
	}
	return candidates;
}

export function notificationMatchesZoneOrRangeFilter(notification, selectedZones, allowedTables) {
	if (isUnassignedNotification(notification)) {
		return true;
	}

	const zones = Array.isArray(selectedZones) ? selectedZones : [];
	const tableSet = allowedTables instanceof Set ? allowedTables : new Set(allowedTables || []);

	if (zones.length === 0 && tableSet.size === 0) {
		return true;
	}

	const normalizedZones = zones.map((z) => String(z).toLowerCase().trim());
	const roomName = notification.reservation?.roomName?.toLowerCase().trim();
	let matchZone = roomName != null && normalizedZones.includes(roomName);

	if (!matchZone && Array.isArray(notification.reservations)) {
		matchZone = notification.reservations.some((res) => {
			const name = res?.roomName?.toLowerCase().trim();
			return name != null && normalizedZones.includes(name);
		});
	}

	if (!matchZone && Array.isArray(notification.rooms)) {
		const roomNames = notification.rooms
			.map((room) => room.name?.toLowerCase().trim())
			.filter(Boolean);
		matchZone = roomNames.some((name) => normalizedZones.includes(name));
	}

	let matchRange = false;
	if (tableSet.size > 0) {
		for (const t of collectNotificationTableNames(notification)) {
			if (tableSet.has(t)) {
				matchRange = true;
				break;
			}
		}
	}

	return matchZone || matchRange;
}

/** Active service reservation (status > 0) without room/table. */
export function hasActiveUnassignedReservations(reservations) {
	if (!Array.isArray(reservations)) return false;
	return reservations.some((r) => r.status > 0 && isUnassignedReservation(r));
}

export function appendUnassignedZoneIfNeeded(zones, reservations) {
	const list = Array.isArray(zones) ? [...zones] : [];
	if (
		hasActiveUnassignedReservations(reservations) &&
		!list.includes(UNASSIGNED_ZONE_KEY)
	) {
		list.push(UNASSIGNED_ZONE_KEY);
	}
	return list;
}
